import { MapPin, Mail, Phone, Trophy, Clock, ExternalLink, FileText } from "lucide-react";

import { cn } from "@/lib/utils";
import { formatMoney } from "@/lib/format";

// Estilo del badge por resultado de adjudicación de un ítem.
const ITEM_RESULT_BADGE: Record<string, { label: string; className: string }> =
  {
    CONTRATADO: {
      label: "Contratado",
      className:
        "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    },
    ADJUDICADO: {
      label: "Adjudicado",
      className:
        "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
    },
    DESIERTO: {
      label: "Desierto",
      className:
        "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
    },
  };

const EMPTY_AWARD =
  "Aún no se ha adjudicado o no se han procesado datos de ganadores para este proceso.";

function ItemResultBadge({ result }: { result: string | null }) {
  const badge = result ? ITEM_RESULT_BADGE[result] : null;
  if (!badge) return null;
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
        badge.className,
      )}
    >
      {badge.label}
    </span>
  );
}

function MatchChip({ keyword }: { keyword: string | null }) {
  return (
    <span
      title={keyword ? `Coincidencia con: ${keyword}` : "Coincidencia"}
      className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-blue-800 dark:bg-blue-900/40 dark:text-blue-200"
    >
      {keyword ?? "Match"}
    </span>
  );
}

// Una línea de ítem dentro de un grupo de ganador: descripción, chip de match,
// monto adjudicado (o referencial si aún no se adjudica) y estado.
function ItemLine({
  item,
  currencySymbol,
}: {
  item: any;
  currencySymbol: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border bg-slate-50/60 px-3 py-2 dark:border-slate-800 dark:bg-slate-800/30">
      <div className="min-w-0">
        <div className="text-[9px] font-semibold text-slate-700 dark:text-slate-300">
          {item.description}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          {item.hasMatch && <MatchChip keyword={item.matchedKeyword} />}
          {item.awardedAmount == null && item.referentialAmount > 0 && (
            <span className="text-[10px] text-slate-400">
              Ref. {formatMoney(item.referentialAmount, currencySymbol)}
            </span>
          )}
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        {item.awardedAmount != null && (
          <span className="font-mono text-xs font-bold text-slate-700 dark:text-slate-200">
            {formatMoney(item.awardedAmount, currencySymbol)}
          </span>
        )}
        <ItemResultBadge result={item.result} />
      </div>
    </div>
  );
}


function WinnerHeader({ group }: { group: any }) {
  return (
    <div className="flex items-center gap-2">
      <Trophy className="size-4 shrink-0 text-amber-500" />
      <div className="min-w-0">
        <div className="truncate text-sm font-extrabold leading-snug text-slate-800 dark:text-slate-100">
          {group.companyId ? (
            <CompanyLink companyId={group.companyId} name={group.name} />
          ) : (
            group.name
          )}
        </div>
        <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400">
          {group.type === "consorcio"
            ? [
                "Consorcio",
                group.ruc ? `RUC ${group.ruc}` : null,
                `${group.members.length} integrante${group.members.length === 1 ? "" : "s"}`,
              ]
                .filter(Boolean)
                .join(" · ")
            : // Un RUC `10…` es una persona, no una empresa: decirle "Empresa"
              // confunde, sobre todo en <=8 UIT donde son más de la mitad.
              group.members[0]?.isPersonaNatural
              ? "Persona natural"
              : "Empresa"}
        </div>
      </div>
    </div>
  );
}

// Ficha de la empresa en pestaña nueva: el detalle de la licitación vive en un
// modal, así que navegar en la misma pestaña haría perder el contexto.
function CompanyLink({
  companyId,
  name,
}: {
  companyId: number;
  name: string;
}) {
  return (
    <a
      href={`/companies/${companyId}`}
      target="_blank"
      rel="noopener noreferrer"
      title="Ver ficha de la empresa"
      className="inline-flex max-w-full items-center gap-1 hover:text-primary hover:underline"
    >
      <span className="truncate">{name}</span>
      <ExternalLink className="size-3 shrink-0 opacity-60" />
    </a>
  );
}

// Quién tramita las fianzas del consorcio, según el contrato o la propuesta técnica.
function GuaranteeBadge({ source }: { source: string | null }) {
  return (
    <>
      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
        Tramita las fianzas
      </span>
      {source && (
        <span className="text-[10px] font-normal text-slate-400">
          según {source === "contrato" ? "el contrato" : "la propuesta técnica"}
        </span>
      )}
    </>
  );
}

// Bloque de contacto de un integrante (RUC + domicilio/correo/teléfono).
function MemberContact({ member }: { member: any }) {
  return (
    <div className="flex flex-col gap-1.5 rounded-xl border bg-slate-50 p-3 text-[11px] leading-snug dark:bg-slate-800/40">
      <div className="flex flex-wrap items-center gap-1.5 font-bold text-slate-700 dark:text-slate-300">
        {member.companyId ? (
          <CompanyLink companyId={member.companyId} name={member.name} />
        ) : (
          member.name
        )}
        {member.handlesGuarantees && <GuaranteeBadge source={member.guaranteeSource} />}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-semibold text-slate-500">
        <span className="flex items-center gap-1">
          <span className="text-slate-400">RUC:</span>
          {member.ruc}
        </span>
        {member.dni && (
          <span className="flex items-center gap-1">
            <span className="text-slate-400">DNI:</span>
            {member.dni}
          </span>
        )}
        {member.share != null && (
          <span className="flex items-center gap-1 text-slate-700 dark:text-slate-200">
            <span className="text-slate-400">Participación:</span>
            {member.share}%
            {member.shareSource && (
              <span className="font-normal text-slate-400">
                · según {member.shareSource === "contrato" ? "el contrato" : "la oferta"}
              </span>
            )}
          </span>
        )}
      </div>
      {(member.address || member.email || member.phone) && (
        <div className="mt-1 flex flex-col gap-1 border-t border-slate-200 pt-1.5 text-slate-500 dark:border-slate-700">
          {member.address && (
            <div className="flex items-start gap-1">
              <MapPin className="mt-0.5 size-3 shrink-0 text-slate-400" />
              <span title={member.address}>{member.address}</span>
            </div>
          )}
          {member.email && (
            <div className="flex items-center gap-1">
              <Mail className="size-3 shrink-0 text-slate-400" />
              <span className="truncate">{member.email}</span>
            </div>
          )}
          {member.phone && (
            <div className="flex items-center gap-1">
              <Phone className="size-3 shrink-0 text-slate-400" />
              <span className="truncate">{member.phone}</span>
            </div>
          )}
          <div className="text-slate-400">Contacto según SUNAT y OECE</div>
        </div>
      )}
    </div>
  );
}

// El representante legal del consorcio: el representante común del contrato o, si
// el contrato no lo trae, el de la oferta en el SEACE.
function RepresentativeLine({
  representative,
  handlesGuarantees,
  guaranteeSource,
}: {
  representative: any;
  handlesGuarantees: boolean;
  guaranteeSource: string | null;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-xl border bg-slate-50 p-3 text-[11px] font-semibold leading-snug text-slate-600 dark:bg-slate-800/40 dark:text-slate-300">
      <span className="text-slate-400">Representante legal:</span>
      <span>
        {representative.name}
        {representative.document ? ` · ${representative.document}` : ""}
      </span>
      <span className="font-normal text-slate-400">
        · según {representative.source === "contrato" ? "el contrato" : "la oferta"}
      </span>
      {handlesGuarantees && <GuaranteeBadge source={guaranteeSource} />}
    </div>
  );
}

const TECHNICAL_OFFER_STATUS: Record<string, (offer: any) => string> = {
  pending: () => "Todavía no terminamos de leer la propuesta técnica.",
  identified: (offer) =>
    `Leímos la propuesta técnica: ${offer.guaranteeManager.name} tramita las fianzas.`,
  notIdentified: () =>
    "Leímos la propuesta técnica y no logramos identificar quién tramita las fianzas.",
  failed: () => "No pudimos leer la propuesta técnica.",
};

// Qué se sacó de la propuesta técnica del consorcio: quién tramita las fianzas y
// el domicilio y correo comunes de su promesa de consorcio.
function TechnicalOfferLine({ technicalOffer }: { technicalOffer: any }) {
  return (
    <div className="flex flex-col gap-1 rounded-xl border bg-slate-50 p-3 text-[11px] leading-snug text-slate-600 dark:bg-slate-800/40 dark:text-slate-300">
      <div className="flex items-start gap-1 font-semibold">
        {technicalOffer.status === "pending" ? (
          <Clock className="mt-0.5 size-3 shrink-0 text-slate-400" />
        ) : (
          <FileText className="mt-0.5 size-3 shrink-0 text-slate-400" />
        )}
        <span>{TECHNICAL_OFFER_STATUS[technicalOffer.status]?.(technicalOffer)}</span>
      </div>
      {technicalOffer.address && (
        <div className="flex items-start gap-1 text-slate-500">
          <MapPin className="mt-0.5 size-3 shrink-0 text-slate-400" />
          <span>Domicilio del consorcio: {technicalOffer.address}</span>
        </div>
      )}
      {technicalOffer.email && (
        <div className="flex items-center gap-1 text-slate-500">
          <Mail className="size-3 shrink-0 text-slate-400" />
          <span className="truncate">Correo del consorcio: {technicalOffer.email}</span>
        </div>
      )}
      {technicalOffer.fileUrl && (
        <a
          href={`${technicalOffer.fileUrl}${technicalOffer.page ? `#page=${technicalOffer.page}` : ""}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex w-fit cursor-pointer items-center gap-1 font-semibold text-primary hover:underline"
        >
          Ver la propuesta técnica
          {technicalOffer.pages ? ` (primeras ${technicalOffer.pages} páginas)` : ""}
          <ExternalLink className="size-3 shrink-0 opacity-60" />
        </a>
      )}
    </div>
  );
}

// Lo que va debajo de los integrantes de un consorcio: su representante y su propuesta técnica.
function ConsortiumExtras({ group }: { group: any }) {
  return (
    <>
      {group.representative && (
        <RepresentativeLine
          representative={group.representative}
          handlesGuarantees={!!group.representativeHandlesGuarantees}
          guaranteeSource={group.representativeGuaranteeSource ?? null}
        />
      )}
      {group.technicalOffer && (
        <TechnicalOfferLine technicalOffer={group.technicalOffer} />
      )}
    </>
  );
}

function UnassignedGroup({
  items,
  currencySymbol,
}: {
  items: any[];
  currencySymbol: string;
}) {
  if (items.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 text-[9px] font-bold uppercase tracking-wider text-slate-400">
        <Clock className="size-3.5" /> Pendiente de adjudicación
      </div>
      <div className="flex flex-col gap-1.5">
        {items.map((it) => (
          <ItemLine key={it.id} item={it} currencySymbol={currencySymbol} />
        ))}
      </div>
    </div>
  );
}

// LAYOUT OBRAS: ganador + sus ítems + contacto, fusionado dentro de Buena Pro.
export function AwardCardWorks({
  winnerGroups,
  unassignedItems,
  currencySymbol,
}: {
  winnerGroups: any[];
  unassignedItems: any[];
  currencySymbol: string;
}) {
  if (winnerGroups.length === 0 && unassignedItems.length === 0) {
    return (
      <p className="py-2 text-center text-xs italic text-slate-400">
        {EMPTY_AWARD}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-5">
      {winnerGroups.map((g) => (
        <div key={g.key} className="flex flex-col gap-3">
          <WinnerHeader group={g} />
          <div className="flex flex-col gap-1.5">
            {g.items.map((it: any) => (
              <ItemLine key={it.id} item={it} currencySymbol={currencySymbol} />
            ))}
          </div>
          <div className="flex flex-col gap-2">
            <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400">
              {g.members.length === 1
                ? "Detalle del Ganador"
                : "Integrantes del Consorcio"}
            </div>
            {g.members.map((m: any, i: number) => (
              <MemberContact key={i} member={m} />
            ))}
            <ConsortiumExtras group={g} />
          </div>
        </div>
      ))}
      <UnassignedGroup
        items={unassignedItems}
        currencySymbol={currencySymbol}
      />
    </div>
  );
}

// LAYOUT BIEN/SERVICIO — protagonista (izquierda): ítems por ganador, sin contacto.
export function WinnerItemsSection({
  winnerGroups,
  unassignedItems,
  currencySymbol,
}: {
  winnerGroups: any[];
  unassignedItems: any[];
  currencySymbol: string;
}) {
  return (
    <div className="flex flex-col gap-3">
      {winnerGroups.map((g) => (
        <div
          key={g.key}
          className="flex flex-col gap-2.5 rounded-2xl border bg-card p-4"
        >
          <WinnerHeader group={g} />
          <div className="flex flex-col gap-1.5">
            {g.items.map((it: any) => (
              <ItemLine key={it.id} item={it} currencySymbol={currencySymbol} />
            ))}
          </div>
        </div>
      ))}
      {unassignedItems.length > 0 && (
        <div className="rounded-2xl border bg-card p-4">
          <UnassignedGroup
            items={unassignedItems}
            currencySymbol={currencySymbol}
          />
        </div>
      )}
    </div>
  );
}

// LAYOUT BIEN/SERVICIO — Buena Pro (derecha): solo contacto por ganador.
export function WinnerContactsCard({ winnerGroups }: { winnerGroups: any[] }) {
  if (winnerGroups.length === 0) {
    return (
      <p className="py-2 text-center text-xs italic text-slate-400">
        {EMPTY_AWARD}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      {winnerGroups.map((g) => (
        <div key={g.key} className="flex flex-col gap-2">
          <WinnerHeader group={g} />
          {g.members.map((m: any, i: number) => (
            <MemberContact key={i} member={m} />
          ))}
          <ConsortiumExtras group={g} />
        </div>
      ))}
    </div>
  );
}

// Quiénes presentaron oferta. Marca al que ganó y a las empresas que ya conoce el usuario.
export function BiddersList({
  bidders,
  knownRucs,
  knownLabel,
}: {
  bidders: any[];
  knownRucs: Set<string>;
  knownLabel: string;
}) {
  if (bidders.length === 0) {
    return (
      <p className="py-2 text-center text-xs italic text-slate-400">
        El reporte de presentación no trae postores.
      </p>
    );
  }
  const severalItems = new Set(bidders.map((b) => b.item)).size > 1;
  return (
    <div className="flex flex-col gap-2">
      <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400">
        {bidders.length} postor{bidders.length === 1 ? "" : "es"} presentaron oferta
      </div>
      {bidders.map((bidder, index) => (
        <div
          key={`${bidder.ruc}-${bidder.item}-${index}`}
          className="flex flex-col gap-1 rounded-xl border bg-slate-50 p-3 text-[11px] leading-snug dark:bg-slate-800/40"
        >
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-bold text-slate-700 dark:text-slate-300">
              {bidder.name}
            </span>
            {bidder.won && (
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                Ganó
              </span>
            )}
            {knownRucs.has(bidder.ruc) && (
              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                {knownLabel}
              </span>
            )}
          </div>
          <div className="font-semibold text-slate-500">
            {[
              severalItems ? `Ítem ${bidder.item}` : null,
              `RUC ${bidder.ruc}`,
              bidder.submittedAt,
              bidder.form,
            ]
              .filter(Boolean)
              .join(" · ")}
          </div>
        </div>
      ))}
      <p className="text-[10px] text-slate-400">
        En un consorcio, el RUC es el del integrante que presentó la oferta.
      </p>
    </div>
  );
}
